import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutGrid } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import {
  VocabularyTermPicker,
  type VocabularyTermValue,
} from '@/components/vocabulary/VocabularyTermPicker';
import { HPO_CATEGORIES } from '@/features/patient-record/data/hpoCategories';
import { cn } from '@/lib/utils';

export type FeatureFilter = 'all' | 'present' | 'absent' | 'unknown';

export interface FilterChip {
  readonly id: FeatureFilter;
  readonly label: string;
  readonly count: number;
}

interface PhenotypesToolbarProps {
  readonly guid: string;
  readonly noFindings: boolean;
  readonly onChangeNoFindings: (next: boolean) => void;
  readonly pickerCategory: string;
  readonly onChangePickerCategory: (next: string) => void;
  readonly groupByCategory: boolean;
  readonly onChangeGroupByCategory: (next: boolean) => void;
  readonly canGroup: boolean;
  readonly onAddPhenotype: (term: VocabularyTermValue | null) => void;
  readonly pickerDisabled: boolean;
  /** HPO ids already attached to the patient — rows for these render disabled (PTV2-1119). */
  readonly excludedHpoIds: ReadonlySet<string>;
  /** Lowercased labels of attached features — blocks "add custom" duplicates (PTV2-1119). */
  readonly excludedLabels: ReadonlySet<string>;
  /** Opens the visual HPO browse dialog (PTV2-1122). */
  readonly onOpenBrowser: () => void;
  readonly browseDisabled: boolean;
  readonly filter: FeatureFilter;
  readonly onChangeFilter: (next: FeatureFilter) => void;
  readonly filterButtons: readonly FilterChip[];
  readonly showFilterChips: boolean;
}

/**
 * Toolbar for the Phenotypes section: "no findings" toggle, category-
 * scoped HPO term picker, "Group by body system" toggle, and the
 * Present/Absent/Unknown filter chips. Extracted from the section
 * orchestrator so the page-level file stays small.
 */
export function PhenotypesToolbar({
  guid,
  noFindings,
  onChangeNoFindings,
  pickerCategory,
  onChangePickerCategory,
  groupByCategory,
  onChangeGroupByCategory,
  canGroup,
  onAddPhenotype,
  pickerDisabled,
  excludedHpoIds,
  excludedLabels,
  onOpenBrowser,
  browseDisabled,
  filter,
  onChangeFilter,
  filterButtons,
  showFilterChips,
}: PhenotypesToolbarProps): ReactElement {
  const { t } = useTranslation();
  const scopedCategoryKey = HPO_CATEGORIES.find(
    (c) => c.id === pickerCategory,
  )?.key;

  return (
    <div className="space-y-3">
      <label className="inline-flex items-center gap-2 text-xs text-muted-foreground select-none">
        <input
          type="checkbox"
          checked={noFindings}
          onChange={(event) => {
            onChangeNoFindings(event.target.checked);
          }}
          className="h-3.5 w-3.5"
          aria-describedby={`no-findings-hint-${guid}`}
        />
        <span className="text-card-foreground font-medium">
          {t('patient.phenotypes.noFindings.label')}
        </span>
        <span id={`no-findings-hint-${guid}`} className="sr-only">
          {t('patient.phenotypes.noFindings.hint')}
        </span>
      </label>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Select
          value={pickerCategory}
          onChange={(event) => {
            onChangePickerCategory(event.target.value);
          }}
          disabled={noFindings}
          className="h-9 w-full sm:w-48 text-sm"
          aria-label={t('patient.phenotypes.categoryPickerLabel')}
        >
          <option value="">{t('patient.phenotypes.categoryPickerAll')}</option>
          {HPO_CATEGORIES.map((c) => (
            <option key={c.id} value={c.id}>
              {t(`patient.phenotypes.categories.${c.key}`)}
            </option>
          ))}
        </Select>
        <div className="flex-1">
          <VocabularyTermPicker
            vocabularyId="hpo"
            {...(pickerCategory.length > 0 ? { subtreeRootId: pickerCategory } : {})}
            value={null}
            onChange={onAddPhenotype}
            placeholder={
              scopedCategoryKey !== undefined
                ? t('patient.phenotypes.picker.placeholderScoped', {
                    category: t(`patient.phenotypes.categories.${scopedCategoryKey}`),
                  })
                : t('patient.phenotypes.picker.placeholder')
            }
            ariaLabel={t('patient.phenotypes.picker.ariaLabel')}
            disabled={pickerDisabled}
            excludedIds={excludedHpoIds}
            excludedLabels={excludedLabels}
            allowCustom
            showInfo
          />
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onOpenBrowser}
          disabled={browseDisabled}
          className="shrink-0 h-9"
        >
          <LayoutGrid className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
          {t('patient.phenotypes.browse.button')}
        </Button>
      </div>
      <label className="inline-flex items-center gap-2 text-xs text-muted-foreground select-none">
        <input
          type="checkbox"
          checked={groupByCategory}
          disabled={!canGroup}
          onChange={(event) => {
            onChangeGroupByCategory(event.target.checked);
          }}
          className="h-3.5 w-3.5"
        />
        <span>{t('patient.phenotypes.groupByBodySystem')}</span>
      </label>
      {showFilterChips && (
        <div
          className="flex items-center gap-0.5 p-0.5 bg-muted rounded-md w-fit"
          role="group"
          aria-label={t('patient.phenotypes.filterAriaLabel')}
        >
          {filterButtons.map(({ id, label, count }) => (
            <button
              key={id}
              type="button"
              onClick={() => {
                onChangeFilter(id);
              }}
              aria-pressed={filter === id}
              disabled={noFindings}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium transition-colors',
                filter === id
                  ? 'bg-card text-card-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-card-foreground',
                noFindings && 'opacity-50 cursor-not-allowed pointer-events-none',
              )}
            >
              {label}
              <span
                className={cn(
                  'rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none tabular-nums',
                  filter === id
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground/60',
                )}
              >
                {count}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
